import { useEffect, useState } from "react";
import { Loader2, Save, Languages, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const NAMES = { en: "English", id: "Bahasa Indonesia" };

export default function LanguageSettings() {
  const { hasPermission } = useAuth();
  const { setLang } = useI18n();
  const canEdit = hasPermission("company_language.edit");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies/me/languages").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/companies/me/languages", {
        ui_language: cfg.ui_language, document_language: cfg.document_language });
      setLang(data.ui_language);
      toast.success("Language settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!cfg) return null;

  const Selector = ({ testid, value, onChange, options }) => (
    <select value={value} disabled={!canEdit} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text disabled:bg-slate-50">
      {options.map((o) => <option key={o} value={o}>{NAMES[o] || o}</option>)}
    </select>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="language-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Governance</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Language Configuration</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Interface language and document language are configured independently.</p>
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="language-save-button"
            className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="mb-3 flex items-center gap-2"><Languages className="h-4 w-4 text-sky-600" /><h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">UI Language</h2></div>
          <p className="mb-3 text-xs text-slate-500">Language of the application interface.</p>
          <Selector testid="ui-language-select" value={cfg.ui_language} options={cfg.supported_ui_languages} onChange={(v) => setCfg((c) => ({ ...c, ui_language: v }))} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-600" /><h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Document Language</h2></div>
          <p className="mb-3 text-xs text-slate-500">Default language for generated documents (used in later phases).</p>
          <Selector testid="document-language-select" value={cfg.document_language} options={cfg.supported_document_languages} onChange={(v) => setCfg((c) => ({ ...c, document_language: v }))} />
        </div>
      </div>
    </div>
  );
}
